import {
  HOSPITALIZATION_ROWS,
  LAST_PCP_ROWS,
  SOCIAL_HISTORY_GROUPS,
  SPECIALIST_OPTIONS,
  buildGroupLine,
} from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/note-forms/readmission-f2f";
import {
  buildCodeAnswers,
  buildFreeTextSection,
  buildOptionSections,
  buildPharmacySection,
  buildRowSection,
} from "./initial-f2f-note-sections";
import {
  NOTE_CONTENT_SECTION_KEYS,
  NOTE_DIAGNOSIS_CODES_ANSWER_KEY,
  NOTE_DIAGNOSIS_FREE_TEXT_ANSWER_KEY,
  NOTE_SCREENING_CUSTOM_ANSWER_KEY,
  NOTE_SCREENING_FREE_TEXT_ANSWER_KEY,
  NOTE_HISTORY_FREE_TEXT_SECTIONS,
  NOTE_ROW_LIST_SEPARATOR,
} from "./initial-f2f-constant";
import { CODE_BLOCKS, SCREENING_BLOCK } from "./initial-f2f-code-constant";
import { buildGroupAnswers, toSection } from "./initial-f2f-note-answers";
import { buildCodeLines, buildGroupLines } from "./initial-f2f-note-lines";
import {
  DateLabelOverrides,
  NoteContent,
  NoteContentFields,
  NoteSection,
  NoteSections,
} from "./initial-f2f-types";
import { buildCodeSummaryLines } from "./initial-f2f-utils";
import { buildVitalsSection } from "./initial-f2f-vitals";
import { buildMiniCogNoteSection } from "./initial-f2f-mini-cog-utils";

const HOSPITALIZATION_LABELS = HOSPITALIZATION_ROWS.map((row) => row.key);
const LAST_PCP_LABELS = LAST_PCP_ROWS.map((row) => row.key);

/** Serialises the note for the encounter-summary `noteContent` field; empty sections are left out. */
export const buildNoteContent = (
  fields: NoteContentFields,
  dateLabels?: DateLabelOverrides
): NoteContent => {
  const hpi = fields.historyOfPresentIllness.trim();
  const entries: [string, NoteSection | undefined][] = [
    [
      NOTE_CONTENT_SECTION_KEYS.hpi,
      hpi ? { text: hpi, lines: [{ text: hpi }] } : undefined,
    ],
    [
      NOTE_CONTENT_SECTION_KEYS.hospitalization,
      buildRowSection(
        HOSPITALIZATION_LABELS,
        fields.hospitalization,
        fields.hospitalizationFreeText
      ),
    ],
    [
      NOTE_CONTENT_SECTION_KEYS.socialHistory,
      toSection(
        buildGroupAnswers(SOCIAL_HISTORY_GROUPS, fields.socialHistory),
        SOCIAL_HISTORY_GROUPS.flatMap((group) =>
          buildGroupLine(group.title, fields.socialHistory[group.key])
        ),
        SOCIAL_HISTORY_GROUPS.flatMap((group) =>
          buildGroupLines(group.title, fields.socialHistory[group.key])
        )
      ),
    ],
    [
      NOTE_CONTENT_SECTION_KEYS.lastPcp,
      buildRowSection(
        LAST_PCP_LABELS,
        fields.lastPcpVisitAndLabs,
        fields.lastPcpVisitFreeText,
        NOTE_ROW_LIST_SEPARATOR
      ),
    ],
    [
      NOTE_CONTENT_SECTION_KEYS.specialist,
      buildRowSection(
        SPECIALIST_OPTIONS,
        fields.currentMedicalSpecialist,
        fields.currentMedicalSpecialistFreeText
      ),
    ],
    ...NOTE_HISTORY_FREE_TEXT_SECTIONS.map(
      (key): [string, NoteSection | undefined] => [
        NOTE_CONTENT_SECTION_KEYS[key],
        buildFreeTextSection(fields.sectionFreeText[key] ?? ""),
      ]
    ),
    [
      NOTE_CONTENT_SECTION_KEYS.medication,
      buildFreeTextSection(fields.medicationFreeText),
    ],
    [
      NOTE_CONTENT_SECTION_KEYS.pharmacy,
      buildPharmacySection(fields.pharmacies),
    ],
    [
      NOTE_CONTENT_SECTION_KEYS.allergy,
      buildFreeTextSection(fields.allergyFreeText),
    ],
    [
      NOTE_CONTENT_SECTION_KEYS.screening,
      toSection(
        buildCodeAnswers(
          NOTE_SCREENING_CUSTOM_ANSWER_KEY,
          NOTE_SCREENING_FREE_TEXT_ANSWER_KEY,
          fields.screeningCustom,
          fields.screeningFreeText
        ),
        buildCodeSummaryLines(
          SCREENING_BLOCK.summaryLabel,
          fields.screeningCustom,
          fields.screeningFreeText
        ),
        buildCodeLines(
          SCREENING_BLOCK.summaryLabel,
          fields.screeningCustom,
          fields.screeningFreeText
        )
      ),
    ],
    [
      NOTE_CONTENT_SECTION_KEYS.diagnosis,
      toSection(
        buildCodeAnswers(
          NOTE_DIAGNOSIS_CODES_ANSWER_KEY,
          NOTE_DIAGNOSIS_FREE_TEXT_ANSWER_KEY,
          fields.diagnosisCodes,
          fields.diagnosisFreeText
        ),
        buildCodeSummaryLines(
          CODE_BLOCKS.diagnosis.summaryLabel,
          fields.diagnosisCodes,
          fields.diagnosisFreeText,
          CODE_BLOCKS.diagnosis.isNumbered
        ),
        buildCodeLines(
          CODE_BLOCKS.diagnosis.summaryLabel,
          fields.diagnosisCodes,
          fields.diagnosisFreeText
        )
      ),
    ],
    [NOTE_CONTENT_SECTION_KEYS.vitalSigns, buildVitalsSection(fields)],
    ...buildOptionSections(fields, dateLabels),
    [
      NOTE_CONTENT_SECTION_KEYS.miniCog,
      buildMiniCogNoteSection(fields.miniCog),
    ],
  ];

  const sections: NoteSections = {};
  entries.forEach(([key, section]) => {
    if (section) sections[key] = section;
  });
  return { sections };
};

export { parseNoteContent } from "./initial-f2f-note-parse";
